import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { X, CreditCard, RefreshCw } from 'lucide-react';
import { auth } from '../firebase';

const API = process.env.REACT_APP_BACKEND_URL || '';
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Appearance config — matches KRO's dark / neutral aesthetic
const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary:    '#111827',
    colorBackground: '#ffffff',
    colorText:       '#111827',
    colorDanger:     '#dc2626',
    fontFamily:      "system-ui, -apple-system, sans-serif",
    fontSizeBase:    '14px',
    spacingUnit:     '4px',
    borderRadius:    '8px',
  },
};


// ── Inner form (lives inside <Elements> provider) ─────────────────────────────
function CardEntryForm({ companyId, leadId, quoteId, mode, invoiceId, paymentIntentId, onSuccess, onClose }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [saving,  setSaving]  = useState(false);
  const [errMsg,  setErrMsg]  = useState('');

  const isCharge = mode === 'charge';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setErrMsg('');

    try {
      if (isCharge) {
        // ── Charge mode: confirm PaymentIntent ──────────────────────────────
        const { paymentIntent, error } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });

        if (error) {
          setErrMsg(error.message || 'Payment failed. Please check the card details.');
          setSaving(false);
          return;
        }

        if (paymentIntent?.status !== 'succeeded') {
          setErrMsg('Payment did not complete. Please try again.');
          setSaving(false);
          return;
        }

        // Notify backend — mark invoice paid
        const token   = await auth.currentUser?.getIdToken(false);
        const confRes = await fetch(`${API}/api/payments/confirm-prepay`, {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ companyId, leadId, quoteId, paymentIntentId, invoiceId }),
        });
        const confData = await confRes.json();

        if (confData.success) {
          onSuccess(confData.invoice);
        } else {
          setErrMsg(confData.detail || 'Payment confirmed but status update failed. Contact support.');
        }

      } else {
        // ── Setup mode: confirm SetupIntent (existing behavior) ─────────────
        const { setupIntent, error } = await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });

        if (error) {
          setErrMsg(error.message || 'Card setup failed.');
          setSaving(false);
          return;
        }

        if (setupIntent?.status !== 'succeeded') {
          setErrMsg('Setup did not complete. Please try again.');
          setSaving(false);
          return;
        }

        const token   = await auth.currentUser?.getIdToken(false);
        const confRes = await fetch(`${API}/api/payments/confirm-setup`, {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ companyId, leadId, setupIntentId: setupIntent.id }),
        });
        const confData = await confRes.json();

        if (confData.success) {
          onSuccess(confData.card);
        } else {
          setErrMsg(confData.detail || 'Card saved in Stripe but metadata update failed.');
        }
      }
    } catch (err) {
      console.error('[CardSetupModal] error:', err);
      setErrMsg('An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Stripe-hosted Payment Element — card data never touches our server */}
      <PaymentElement
        options={{ layout: 'tabs' }}
        data-testid="payment-element"
      />

      {errMsg && (
        <div
          className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700"
          data-testid="card-setup-error"
        >
          {errMsg}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || saving}
          data-testid={isCharge ? 'charge-card-btn' : 'save-card-btn'}
          className="px-5 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition"
        >
          {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
          {saving
            ? (isCharge ? 'Processing…' : 'Saving…')
            : (isCharge ? 'Charge Card' : 'Save Card')
          }
        </button>
      </div>
    </form>
  );
}


// ── Outer modal — fetches intent client_secret, then renders Elements ─────────
export default function CardSetupModal({
  companyId, leadId, onSuccess, onClose,
  // charge mode props
  mode = 'setup',   // 'setup' | 'charge'
  quoteId = null,
  prepayAmount = null,
}) {
  const [clientSecret,    setClientSecret]    = useState('');
  const [invoiceId,       setInvoiceId]       = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [loadErr,         setLoadErr]         = useState('');
  // Stripe instance is created dynamically once we know whether the company
  // has a connected account — direct-charge intents require loadStripe to be
  // called with { stripeAccount: 'acct_xxx' } or Elements won't render.
  const [stripeInstance,  setStripeInstance]  = useState(null);

  const isCharge = mode === 'charge';

  const fetchIntent = useCallback(async () => {
    setLoadErr('');
    try {
      const token    = await auth.currentUser?.getIdToken(false);
      const endpoint = isCharge
        ? `${API}/api/payments/prepay-intent`
        : `${API}/api/payments/setup-intent`;

      const res  = await fetch(endpoint, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, leadId, ...(isCharge ? { quoteId } : {}) }),
      });
      const data = await res.json();

      if (data.success && data.clientSecret) {
        // Build the correct Stripe.js instance for this company's account context.
        // For direct-charge connected accounts the intent lives on the connected
        // account, so we MUST pass { stripeAccount } or PaymentElement stays blank.
        const connectAccountId = data.connectAccountId || '';
        const sp = connectAccountId && STRIPE_PK
          ? loadStripe(STRIPE_PK, { stripeAccount: connectAccountId })
          : loadStripe(STRIPE_PK);
        setStripeInstance(sp);

        setClientSecret(data.clientSecret);
        if (isCharge) {
          setInvoiceId(data.invoiceId || '');
          setPaymentIntentId(data.paymentIntentId || '');
        }
      } else {
        setLoadErr(data.detail || `Could not initialize ${isCharge ? 'payment' : 'card setup'}. Try again.`);
      }
    } catch {
      setLoadErr(`Network error initializing ${isCharge ? 'payment' : 'card setup'}. Please retry.`);
    }
  }, [companyId, leadId, quoteId, isCharge]);

  useEffect(() => { fetchIntent(); }, [fetchIntent]);

  const headerLabel = isCharge ? 'Collect Prepay Payment' : 'Add Card on File';
  const amountLabel = isCharge && prepayAmount ? ` — $${Number(prepayAmount).toFixed(2)}` : '';

  return (
    // Overlay
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
        data-testid={isCharge ? 'prepay-collect-modal' : 'card-setup-modal'}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-gray-700" />
            <span className="font-semibold text-gray-900">
              {headerLabel}{amountLabel}
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100 transition"
            aria-label="Close"
            data-testid="close-card-modal-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {loadErr && (
            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4">
              {loadErr}
            </div>
          )}

          {!clientSecret && !loadErr && (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
              <RefreshCw className="w-4 h-4 animate-spin" />
              {isCharge ? 'Initializing secure payment…' : 'Initializing secure card entry…'}
            </div>
          )}

          {clientSecret && stripeInstance && (
            <Elements
              stripe={stripeInstance}
              options={{ clientSecret, appearance: STRIPE_APPEARANCE }}
            >
              <CardEntryForm
                companyId={companyId}
                leadId={leadId}
                quoteId={quoteId}
                mode={mode}
                invoiceId={invoiceId}
                paymentIntentId={paymentIntentId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            </Elements>
          )}
        </div>

        {/* Footer note */}
        <div className="px-6 pb-4 text-xs text-gray-400 text-center border-t border-gray-50 pt-3">
          Card details are entered directly into Stripe's secure form — never stored on KRO servers.
        </div>
      </div>
    </div>
  );
}
